import Animated, { clamp, interpolate, useAnimatedStyle, useSharedValue } from 'react-native-reanimated';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { View } from 'react-native';
import { useEffect, useRef, useState } from 'react';
import { styles } from './SliderAdjustments.style';
import { brand } from 'src/theme/colors';
import { useSliderAdjustmentsDimension } from './useSliderAdjustmentsDimension';
import { BaseButton } from '@photosi/rn-components/buttons/base-button/BaseButton';
import { generateTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { SliderAdjustmentsProps } from './SliderAdjustments.types';

export const SliderAdjustments = ({
  minValue,
  maxValue,
  defaultValue,
  onChange,
  onChangeEnd,
  testID,
  sliderWidth,
  focusGroup,
  focusOrder,
  focusId,
  focusable,
  accessible = true,
  accessibilityRole,
  accessibilityLabel,
}: SliderAdjustmentsProps) => {
  const {
    SLIDER_CONTAINER_HEIGHT,
    SLIDER_ADJUSTMENTS_MAX_VALUE,
    SLIDER_ADJUSTMENTS_MIN_VALUE,
    SLIDER_DOT_WIDTH,
    SLIDER_WIDTH,
  } = useSliderAdjustmentsDimension();

  minValue = minValue ?? SLIDER_ADJUSTMENTS_MIN_VALUE;
  maxValue = maxValue ?? SLIDER_ADJUSTMENTS_MAX_VALUE;

  const WIDTH_SLIDER = sliderWidth ?? SLIDER_WIDTH;
  const SLIDER_MAX_VALUE = WIDTH_SLIDER - SLIDER_DOT_WIDTH;
  const [hover, setHover] = useState<boolean>(false);
  const [drag, setDrag] = useState<boolean>(false);

  // Tracks if gesture started over the indicator
  const startedOnHandle = useRef(false);

  // Flag to track if we're actually moving the slider
  const isMoving = useRef(false);

  const sliderSV = useSharedValue(
    clamp(interpolate(defaultValue, [minValue, maxValue], [0, SLIDER_MAX_VALUE]), 0, SLIDER_MAX_VALUE),
  );

  useEffect(() => {
    sliderSV.value = clamp(interpolate(defaultValue, [minValue, maxValue], [0, SLIDER_MAX_VALUE]), 0, SLIDER_MAX_VALUE);
  }, [defaultValue]);

  const sliderStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateX: sliderSV.value }],
    };
  });

  const sliderHoverStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateX: sliderSV.value - 11 }],
    };
  });

  const sliderTrackStyle = useAnimatedStyle(() => {
    const actualValue = sliderSV.value + SLIDER_DOT_WIDTH - (WIDTH_SLIDER / 2 + 11);
    const positionStyle = actualValue > 0 ? styles.sliderTrackActivePositive : styles.sliderTrackActiveNegative;
    return {
      width: Math.abs(actualValue),
      ...positionStyle,
    };
  });

  // Tracks if x position is over the indicator
  const isPositionOverHandle = (x: number, handlePosition: number) => {
    'worklet';
    const tolerance = SLIDER_DOT_WIDTH + 20;
    const handleStart = handlePosition - tolerance;
    const handleEnd = handlePosition + tolerance;
    return x >= handleStart && x <= handleEnd;
  };

  // Tracks if position x is on the center of the indicator
  const isPositionAtHandleCenter = (x: number, handlePosition: number) => {
    'worklet';
    const handleCenter = handlePosition + SLIDER_DOT_WIDTH / 2;
    const tolerance = 5; // Slight tolerance over indicator center
    return Math.abs(x - handleCenter) <= tolerance;
  };

  // Tracks if position x is 'inside' slider width area
  const isPositionInSliderArea = (x: number) => {
    'worklet';
    return x >= 0 && x <= SLIDER_MAX_VALUE + SLIDER_DOT_WIDTH;
  };

  const panSlider = Gesture.Pan()
    .minDistance(1)
    .onStart(event => {
      const handlePosition = sliderSV.value;
      startedOnHandle.current = isPositionOverHandle(event.x, handlePosition);

      if (startedOnHandle.current) {
        isMoving.current = true;
      }

      setDrag(true);
    })
    .onChange(event => {
      if (!startedOnHandle.current) return;

      if (isMoving.current) {
        const isInSliderArea = isPositionInSliderArea(event.x);

        if (isInSliderArea) {
          const newPosition = clamp(sliderSV.value + event.changeX, 0, SLIDER_MAX_VALUE);
          sliderSV.value = newPosition;

          const value = interpolate(sliderSV.value, [0, SLIDER_MAX_VALUE], [minValue, maxValue]);
          onChange(value);
        } else {
          isMoving.current = false;
        }
      } else {
        const handlePosition = sliderSV.value;
        const isCentered = isPositionAtHandleCenter(event.x, handlePosition);

        if (isCentered) {
          isMoving.current = true;
        }
      }
    })
    .onEnd(event => {
      if (startedOnHandle.current) {
        const value = interpolate(sliderSV.value, [0, SLIDER_MAX_VALUE], [minValue, maxValue]);
        onChangeEnd && onChangeEnd(value);
      }
      setDrag(false);
      startedOnHandle.current = false;
      isMoving.current = false;
    })
    .onFinalize(() => {
      setDrag(false);
      startedOnHandle.current = false;
      isMoving.current = false;
    });

  const tap = Gesture.Tap().onEnd(event => {
    sliderSV.value = clamp(event.x - SLIDER_DOT_WIDTH / 2, 0, SLIDER_MAX_VALUE);

    const value = interpolate(sliderSV.value, [0, SLIDER_MAX_VALUE], [minValue, maxValue]);
    onChangeEnd && onChangeEnd(value);
  });

  return (
    <GestureDetector gesture={Gesture.Race(panSlider, tap)}>
      <View style={[styles.sliderContainer, { height: SLIDER_CONTAINER_HEIGHT }]}>
        <View style={[styles.sliderTrack, { width: WIDTH_SLIDER }]} testID={testID ?? 'slider'}>
          {/*  <GestureDetector
            gesture={Gesture.Hover()
              .onStart(() => setHover(true))
              .onEnd(() => setHover(false))}
          > */}
          <Animated.View
            style={[styles.sliderHandle, sliderStyle, { width: SLIDER_DOT_WIDTH, height: SLIDER_DOT_WIDTH }]}
            onPointerEnter={() => setHover(true)}
            onPointerLeave={() => setHover(false)}
          >
            <BaseButton
              style={[styles.button, { width: SLIDER_DOT_WIDTH + 8, height: SLIDER_DOT_WIDTH + 8 }]}
              testID={generateTestID(testID, TestIDs.SLIDER_DOT)}
              focusStyle={styles.buttonFocus}
              focusId={focusId}
              focusOrder={focusOrder}
              focusGroup={focusGroup}
              focusable={focusable}
              onKeyDown={async (event: KeyboardEvent) => {
                if (event.code === 'ArrowRight' || event.code === 'ArrowLeft') {
                  event.preventDefault();
                  event.stopPropagation();
                  sliderSV.value = event.code === 'ArrowRight' ? sliderSV.value + 1 : sliderSV.value - 1;
                  const value = interpolate(sliderSV.value, [0, SLIDER_MAX_VALUE], [minValue, maxValue]);
                  onChangeEnd && onChangeEnd(value);
                }
              }}
              accessible={accessible}
              accessibilityLabel={accessibilityLabel ?? `slider adjustments ${focusId ?? testID}`}
              accessibilityRole={accessibilityRole ?? 'slider'}
            >
              {() => <></>}
            </BaseButton>
          </Animated.View>
          {/* </GestureDetector> */}
          {hover && (
            <Animated.View
              style={[
                styles.sliderHandleHover,
                sliderHoverStyle,
                { width: SLIDER_CONTAINER_HEIGHT, height: SLIDER_CONTAINER_HEIGHT },
                drag && { backgroundColor: `${brand.main.water}4c` },
              ]}
            />
          )}
          <Animated.View style={[styles.sliderTrackActive, sliderTrackStyle]} />
        </View>
      </View>
    </GestureDetector>
  );
};
